import classNames from 'classnames';
import React from 'react';
import checkIcon from '../icons/non-prefilled/16x16/check.svg';
import addTestId from '../utils/addTestId';
import { Link } from '../utils/router/v3Bridge';
import isExternalUrl from '../utils/url/isExternalUrl';
import type { CaretDirection } from './Caret';
import Caret from './Caret';
import styles from './Menu.module.scss';
import SVGIcon from './SVGIcon';
import Tooltip from './Tooltip';
import PopOver from './deprecated/PopOver';
import PopOverContainer from './deprecated/PopOverContainer';

interface BasicMenuItemProps {
  caretDirection?: CaretDirection;
  children: React.ReactNode;
  danger?: boolean;
  disabled?: boolean;
  icon?: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  right?: React.ReactNode;
  selected?: boolean;
  target?: string;
  to?: string;
  tooltip?: string;
  tooltipPlacement?: React.ComponentProps<typeof Tooltip>['placement'];
  wrap?: boolean;
  rel?: string;
}

const BasicMenuItem = ({
  caretDirection,
  children,
  danger = false,
  disabled = false,
  icon,
  onClick,
  right,
  selected = false,
  target,
  to,
  tooltip,
  tooltipPlacement = 'left',
  wrap = false,
  rel,
}: BasicMenuItemProps) => {
  let rightElement = null;

  if (right) {
    rightElement = right;
  } else if (selected) {
    rightElement = (
      <SVGIcon className={styles.check} size="small">
        {checkIcon}
      </SVGIcon>
    );
  } else if (caretDirection) {
    rightElement = <Caret inline direction={caretDirection} size="small" />;
  }

  const inner = (
    <>
      {!!icon &&
        (typeof icon === 'string' ? (
          <SVGIcon className={styles.icon}>{icon}</SVGIcon>
        ) : (
          <div className={styles.icon}>{icon}</div>
        ))}
      <div
        className={classNames(styles.main, {
          [styles.wrap]: wrap,
        })}
      >
        {children}
      </div>
      {!!rightElement && <div className={styles.right}>{rightElement}</div>}
    </>
  );

  const className = classNames(styles.item, {
    [styles.danger]: danger,
    [styles.disabled]: disabled,
    [styles.selected]: selected,
  });

  if (to && isExternalUrl(to)) {
    return (
      <a
        className={className}
        href={to}
        onClick={onClick}
        target={target}
        rel={rel}
      >
        {inner}
      </a>
    );
  }

  const item = to ? (
    <Link
      className={className}
      onClick={onClick}
      to={to}
      target={target}
      rel={rel}
    >
      {inner}
    </Link>
  ) : (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- TODO - fix next time this file is edited
    <div className={className} onClick={onClick} tabIndex={-1}>
      {inner}
    </div>
  );
  return tooltip ? (
    <Tooltip placement={tooltipPlacement} content={tooltip} display="block">
      {item}
    </Tooltip>
  ) : (
    item
  );
};

interface MobileExpandableMenuItemProps {
  subMenuItems: React.ReactNode;
  children: React.ReactNode;
}

class MobileExpandableMenuItem extends React.Component<
  MobileExpandableMenuItemProps,
  { isExpanded: boolean }
> {
  constructor(props: MobileExpandableMenuItemProps) {
    super(props);
    this.state = { isExpanded: false };
  }

  render() {
    const { subMenuItems, ...props } = this.props;
    const { isExpanded } = this.state;
    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      this.setState({ isExpanded: !isExpanded });
    };

    return (
      <>
        <BasicMenuItem
          caretDirection={isExpanded ? 'up' : 'down'}
          {...props}
          onClick={handleClick}
        />
        {isExpanded && (
          <div className={styles.expandedSubMenuItems}>{subMenuItems}</div>
        )}
      </>
    );
  }
}

interface MenuItemProps extends BasicMenuItemProps {
  id?: string;
  onClick?: (e?: React.SyntheticEvent) => unknown;
  subMenuItems?: React.ReactNode;
  testId?: string;
  hidden?: boolean;
}

// eslint-disable-next-line react/no-multi-comp
export const MenuItem = React.forwardRef<HTMLDivElement, MenuItemProps>(
  ({ id, subMenuItems, testId, hidden, ...props }: MenuItemProps, ref) => (
    <div
      ref={ref}
      className={styles.itemOuter}
      {...addTestId(testId)}
      id={id}
      role="menuitem"
      style={hidden ? { display: 'none' } : undefined}
    >
      {subMenuItems ? (
        <>
          <div className={styles.desktopItemInner}>
            <PopOver
              // eslint-disable-next-line @typescript-eslint/no-use-before-define
              popOver={<Menu>{subMenuItems}</Menu>}
              trigger="hover"
              responsivePlacement="bottom"
              placement="right-start"
            >
              <BasicMenuItem
                caretDirection="right"
                {...props}
                onClick={(e) => {
                  e.stopPropagation();
                  if (props.onClick) {
                    props.onClick();
                  }
                }}
              />
            </PopOver>
          </div>
          <div className={styles.mobileItemInner}>
            <MobileExpandableMenuItem subMenuItems={subMenuItems} {...props} />
          </div>
        </>
      ) : (
        <BasicMenuItem {...props} />
      )}
    </div>
  ),
);

interface MenuSimpleItemProps {
  children: React.ReactNode;
}

export const MenuSimpleItem = ({ children }: MenuSimpleItemProps) => (
  <div className={styles.simpleItem}>{children}</div>
);

interface MenuSeparatorProps {
  fullWidth?: boolean;
}

export const MenuSeparator = ({ fullWidth = false }: MenuSeparatorProps) => (
  <div
    className={classNames(styles.separator, {
      [styles.fullWidth]: fullWidth,
    })}
  />
);

interface MenuProps {
  children: React.ReactNode;
  ariaLabel?: string;
}

const Menu = ({ children, ariaLabel }: MenuProps) =>
  children ? (
    <PopOverContainer noPadding width="auto" fullHeight>
      <div
        data-no-print
        className={styles.menu}
        role="menu"
        aria-label={ariaLabel}
      >
        {children}
      </div>
    </PopOverContainer>
  ) : null;

export default Menu;
